import { useEffect, useId, useRef, useState, type ClipboardEvent, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { canWrite, isEmployee, useSession, useTenant } from '../../auth/session';
import { useCompanyPractice } from '../../pages/team/Accountant';
import { fmtDate } from '../../lib/format';
import { BRAND } from '../../lib/brand';
import { APP_VERSION, browserName, type HelpView, type RequestType } from '../../lib/help';
import { errorText, Icon } from '../../ui/kit';
import {
  helpLocale,
  pageName,
  replyTo,
  screenshotProblem,
  sendRequest,
  statusLabel,
  TYPE_FIELDS,
  TYPE_PLACEHOLDER,
  TYPE_SUB,
  TYPE_TITLE,
  useArticle,
  useArticles,
  useMarkWhatsNewSeen,
  useSupportRequest,
  useSupportRequests,
  useSupportStatus,
  useWhatsNew,
  type SupportRequest,
} from './data';
import { AssistantView } from './AssistantView';
import { useAssistantUsage } from './assistant';
import './help.css';

// The help panel (APP-DESIGN.md section 10): a 440px slide-over on the right, over a light dim of
// the page, which keeps its state underneath. Escape and the close button close it; focus moves into
// the panel and back to what opened it.

const TYPES: RequestType[] = ['bug', 'howto', 'billing', 'feature'];
/** Shown when Zendesk is not set up yet, as the old Help menu did. */
const SUPPORT_EMAIL = 'support@mybooks.app';

export function HelpPanel({ view, onView, onClose }: { view: HelpView; onView: (v: HelpView) => void; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const returnTo = document.activeElement as HTMLElement | null;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.stopPropagation(); closeRef.current(); return; }
      // Focus stays in the panel while it is open.
      if (e.key !== 'Tab' || !ref.current) return;
      const items = [...ref.current.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])')];
      if (items.length === 0) return;
      const first = items[0]!, last = items[items.length - 1]!;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('keydown', onKey, true); if (returnTo && document.contains(returnTo)) returnTo.focus(); };
  }, []);
  // Each view starts with focus on its first control.
  useEffect(() => {
    ref.current?.querySelector<HTMLElement>('[data-autofocus], input, textarea, button')?.focus();
  }, [view]);

  const home = () => onView({ kind: 'home' });
  let title = 'Help';
  let body: ReactNode;
  switch (view.kind) {
    case 'home': body = <HomeView initialQuery={view.query ?? ''} onView={onView} onClose={onClose} />; break;
    case 'form': title = TYPE_TITLE[view.type]; body = <FormView key={view.type} type={view.type} prefill={view.prefill} onView={onView} />; break;
    case 'request': title = 'Your request'; body = <RequestView id={view.id} />; break;
    case 'article': title = 'Help article'; body = <ArticleView id={view.id} />; break;
    case 'whats-new': title = "What's new"; body = <WhatsNewView />; break;
    case 'assistant': title = `${BRAND.name} Assistant`; body = <AssistantView key={view.question ?? ''} question={view.question} mode={view.mode} onView={onView} />; break;
  }

  return createPortal(
    <div className="help-layer">
      <div className="help-dim" onMouseDown={onClose} aria-hidden="true" />
      <div ref={ref} className="help-panel" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="help-head">
          {view.kind !== 'home' && <button type="button" className="link-btn help-back" onClick={home}>← Help</button>}
          <h2 id={titleId}>{title}</h2>
          <button type="button" className="btn ghost small help-close" onClick={onClose} aria-label="Close help"><Icon name="x" /></button>
        </div>
        <div className="help-body">{body}</div>
      </div>
    </div>,
    document.body,
  );
}

// ---- Home ----

function useDebounced(value: string, ms: number) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function HomeView({ initialQuery, onView, onClose }: { initialQuery: string; onView: (v: HelpView) => void; onClose: () => void }) {
  const location = useLocation();
  const tenant = useTenant();
  const status = useSupportStatus();
  const configured = status.data?.configured ?? false;
  const assistant = useAssistantUsage();
  // The assistant: help answers need the Help Center too; questions about the books do not.
  const canAsk = (assistant.data?.help ?? false) || (assistant.data?.books ?? false);
  const askMode = assistant.data?.help ? 'help' : 'books';
  const [query, setQuery] = useState(initialQuery);
  const q = useDebounced(query.trim(), 250);
  const searching = q.length >= 2;
  const articles = useArticles(searching ? q : '', location.pathname, configured);
  const requests = useSupportRequests('all', 2);
  const whatsNew = useWhatsNew();
  const state = status.data?.status.state ?? 'operational';
  const stateText = state === 'operational' ? 'All systems working' : state === 'degraded' ? 'Some systems are slow' : 'Some systems are down';

  return (
    <>
      {(configured || canAsk) && (
        <section className="help-section" aria-label="Search help">
          <form className={`help-search ${canAsk ? 'ask' : ''}`} onSubmit={(e) => { e.preventDefault(); if (canAsk && query.trim()) onView({ kind: 'assistant', question: query.trim(), mode: askMode }); }}>
            {canAsk ? <span className="assistant-mark" aria-hidden="true">✦</span> : <Icon name="search" />}
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={canAsk ? (configured ? 'Ask a question or search help' : 'Ask about your books') : 'Search help, e.g. "reconcile"'} aria-label={canAsk ? (configured ? 'Ask a question or search help' : 'Ask about your books') : 'Search help'} maxLength={200} data-autofocus />
            {canAsk && <button type="submit" className="link-btn help-ask" disabled={!query.trim()}>Ask</button>}
          </form>
          {configured && <><h3 className="help-h">{searching ? 'Results' : 'Popular'}</h3>
          {articles.isLoading ? <p className="help-muted">Loading…</p>
            : articles.isError ? <p className="help-muted" role="alert">Help articles did not load. {errorText(articles.error)}</p>
              : (articles.data?.articles.length ?? 0) === 0 ? <p className="help-muted">{searching ? `Nothing matches “${q}”. Try other words, or ask us below.` : 'No articles yet.'}</p>
                : (
                  <ul className="help-list">
                    {articles.data!.articles.map((a) => (
                      <li key={a.id}><button type="button" className="help-row" onClick={() => onView({ kind: 'article', id: a.id })}>
                        <span className="help-row-title">{a.title}</span>{searching && a.snippet && <span className="help-row-sub">{a.snippet}</span>}
                      </button></li>
                    ))}
                  </ul>
                )}</>}
        </section>
      )}

      <section className="help-section" aria-labelledby="help-contact">
        <h3 className="help-h" id="help-contact">Contact us</h3>
        <ul className="help-list">
          {TYPES.map((t) => (
            <li key={t}><button type="button" className="help-row" onClick={() => onView({ kind: 'form', type: t })}>
              <span className="help-row-title">{TYPE_TITLE[t]}</span><span className="help-row-sub">{TYPE_SUB[t]}</span>
            </button></li>
          ))}
        </ul>
        {status.data && !configured && <p className="help-muted">Requests from the app are not switched on yet. Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.</p>}
      </section>

      {(requests.data?.requests.length ?? 0) > 0 && (
        <section className="help-section" aria-labelledby="help-yours">
          <div className="help-h-row">
            <h3 className="help-h" id="help-yours">Your requests</h3>
            {!isEmployee(tenant.role) && <Link className="link-btn" to="/settings?tab=requests" onClick={onClose}>All</Link>}
          </div>
          <ul className="help-list">
            {requests.data!.requests.map((r) => <li key={r.id}><RequestRow r={r} onOpen={() => onView({ kind: 'request', id: r.id })} /></li>)}
          </ul>
        </section>
      )}

      <footer className="help-foot">
        <span className={`help-state ${state}`}>
          {status.data?.status.url ? <a href={status.data.status.url} target="_blank" rel="noreferrer">{stateText}</a> : stateText}
        </span>
        <button type="button" className="link-btn" onClick={() => onView({ kind: 'whats-new' })}>
          What's new{whatsNew.data?.unseen && <span className="help-new-dot" aria-label=", new" />}
        </button>
      </footer>
    </>
  );
}

/** One request: title, a short detail line and its status (dot + word). */
export function RequestRow({ r, onOpen }: { r: SupportRequest; onOpen: () => void }) {
  const s = statusLabel(r);
  return (
    <button type="button" className="help-row help-request" onClick={onOpen}>
      <span className="help-row-main">
        <span className="help-row-title">{r.subject}{r.has_update && <span className="help-new-dot" aria-label=", has an update" />}</span>
        <span className="help-row-sub">#{r.number} · {TYPE_TITLE[r.type]} · {fmtDate(r.last_activity_at.slice(0, 10))}</span>
      </span>
      <span className={`pill status ${s.cls}`}>{s.label}</span>
    </button>
  );
}

// ---- Form ----

function FormView({ type, prefill, onView }: { type: RequestType; prefill: { subject?: string; body?: string; errorId?: string; assistantConversationId?: string } | undefined; onView: (v: HelpView) => void }) {
  const location = useLocation();
  const tenant = useTenant();
  const session = useSession();
  const queryClient = useQueryClient();
  const practice = useCompanyPractice();
  const [subject, setSubject] = useState(prefill?.subject ?? '');
  const [body, setBody] = useState(prefill?.body ?? '');
  const [shot, setShot] = useState<File | null>(null);
  const [shotError, setShotError] = useState<string | null>(null);
  const [access, setAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState<{ request: SupportRequest; until: string | null } | null>(null);
  const [first, second] = TYPE_FIELDS[type];
  const page = pageName(location.pathname);
  const browser = browserName(navigator.userAgent);
  const company = tenant.name;
  const mayGiveAccess = (type === 'bug' || type === 'howto') && canWrite(tenant.role);
  const linked = (practice.data ?? []).find((p) => !p.via_practice && (p.state === 'active' || p.state === 'practice_managed'));
  const contextLine = [page, company, tenant.role.replace('_', ' '), browser, `version ${APP_VERSION}`, prefill?.errorId, prefill?.assistantConversationId && 'your conversation with the assistant'].filter(Boolean).join(' · ');

  const pick = (file: File | null | undefined) => {
    if (!file) return;
    const problem = screenshotProblem(file);
    setShotError(problem);
    setShot(problem ? null : file);
  };
  const onPaste = (e: ClipboardEvent) => {
    if (type !== 'bug') return;
    const file = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
    if (file) { e.preventDefault(); pick(file); }
  };
  const send = async () => {
    if (!subject.trim() || !body.trim()) { setError(`Fill in "${!subject.trim() ? first : second}".`); return; }
    setBusy(true);
    setError(null);
    try {
      const r = await sendRequest({ type, subject: subject.trim(), body: body.trim(), page: location.pathname === '/' ? 'Dashboard' : `${page} (${location.pathname})`, browser, appVersion: APP_VERSION, errorId: prefill?.errorId, booksAccess: mayGiveAccess && access, screenshot: shot, assistantConversationId: prefill?.assistantConversationId });
      setSent({ request: r.request, until: r.books_access_until });
      void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[1] === 'support' });
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="help-sent" role="status">
        <p className="help-sent-title"><Icon name="check" /> Sent. Request #{sent.request.number}</p>
        <p className="help-muted">We reply by email to {session.user?.email ?? 'you'} and under Your requests.</p>
        {sent.until && <p className="help-muted">{BRAND.name} support can see {company}'s books until {fmtDate(sent.until.slice(0, 10))}. You can remove access in Settings → Team.</p>}
        <div className="help-actions">
          <button type="button" className="btn" onClick={() => onView({ kind: 'request', id: sent.request.id })}>View request</button>
          <button type="button" className="btn primary" onClick={() => onView({ kind: 'home' })}>Done</button>
        </div>
      </div>
    );
  }

  return (
    <form className="help-form" onSubmit={(e) => { e.preventDefault(); void send(); }} onPaste={onPaste} noValidate>
      <div className="field">
        <label htmlFor="help-subject">{first}</label>
        <input id="help-subject" className="input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={200} required data-autofocus />
      </div>
      <div className="field">
        <label htmlFor="help-body">{second}</label>
        <textarea id="help-body" className="input" value={body} onChange={(e) => setBody(e.target.value)} maxLength={10000} required placeholder={TYPE_PLACEHOLDER[type]} />
      </div>
      {type === 'bug' && (
        <div className="field">
          <span className="help-label">Screenshot</span>
          {shot ? (
            <div className="help-file"><Icon name="upload" /><span className="help-file-name" title={shot.name}>{shot.name || 'Pasted image'}</span><button type="button" className="link-btn" onClick={() => setShot(null)}>Remove</button></div>
          ) : (
            <label className="help-file help-file-pick">
              <Icon name="upload" /><span>Add a screenshot, or paste one here</span>
              <input type="file" accept="image/png,image/jpeg" className="visually-hidden" onChange={(e) => pick(e.target.files?.[0])} />
            </label>
          )}
          {shotError ? <span className="error" role="alert">{shotError}</span> : <span className="hint">One PNG or JPEG, up to 10 MB.</span>}
        </div>
      )}
      {mayGiveAccess && (
        <label className="help-check">
          <input type="checkbox" checked={access} onChange={(e) => setAccess(e.target.checked)} />
          <span>Let {BRAND.name} support see {company}'s books for 7 days to look into this. You can remove access at any time.</span>
        </label>
      )}
      {type === 'howto' && (
        <p className="help-note">
          Tax question? {linked
            ? <>Ask <Link to="/from-your-accountant">{linked.practice_name}</Link>.</>
            : 'Ask your accountant.'} {BRAND.name} support can help with the software, not with tax decisions.
        </p>
      )}
      <p className="help-context" title="Attached to your request so we can help faster. Never balances, transactions or customer details.">Sent with: {contextLine}</p>
      {error && <p className="help-error" role="alert">{error}</p>}
      <div className="help-form-foot">
        <span className="help-muted">We reply by email and under Your requests.</span>
        <button type="submit" className="btn primary" disabled={busy}>{busy ? 'Sending…' : 'Send'}</button>
      </div>
    </form>
  );
}

// ---- One request ----

function RequestView({ id }: { id: string }) {
  const request = useSupportRequest(id);
  const queryClient = useQueryClient();
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Opening it marks it seen on the server; the lists follow.
  useEffect(() => {
    if (request.data) void queryClient.invalidateQueries({ predicate: (q) => q.queryKey[1] === 'support' && q.queryKey[2] === 'requests' });
  }, [request.data, queryClient]);

  if (request.isLoading) return <p className="help-muted">Loading…</p>;
  if (request.isError || !request.data) return <p className="help-error" role="alert">This request did not load. {errorText(request.error)}</p>;
  const r = request.data;
  const s = statusLabel(r);
  const send = async () => {
    if (!reply.trim()) return;
    setBusy(true); setError(null);
    try {
      await replyTo(r.id, reply.trim());
      setReply('');
      await request.refetch();
    } catch (e) { setError(errorText(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <div className="help-request-head">
        <h3>{r.subject}</h3>
        <p className="help-row-sub">Request #{r.number} · {TYPE_TITLE[r.type]} · <span className={`pill status ${s.cls}`}>{s.label}</span></p>
      </div>
      <ol className="help-thread">
        {r.comments.map((m) => (
          <li key={m.id} className={m.from === 'you' ? 'you' : 'support'}>
            <div className="help-thread-who">{m.from === 'you' ? 'You' : `${m.author_name ?? BRAND.name} · ${BRAND.name} support`} · {fmtDate(m.created_at.slice(0, 10))}</div>
            <div className="help-thread-body">{m.body}</div>
          </li>
        ))}
      </ol>
      <form className="help-form" onSubmit={(e) => { e.preventDefault(); void send(); }}>
        <div className="field">
          <label htmlFor="help-reply">Reply</label>
          <textarea id="help-reply" className="input short" value={reply} onChange={(e) => setReply(e.target.value)} maxLength={10000} />
        </div>
        {error && <p className="help-error" role="alert">{error}</p>}
        <div className="help-form-foot"><span /><button type="submit" className="btn primary" disabled={busy || !reply.trim()}>{busy ? 'Sending…' : 'Send reply'}</button></div>
      </form>
    </>
  );
}

// ---- Article ----

/** The article in a sandboxed frame: Zendesk's HTML never runs script or touches the app. */
function articleDocument(title: string, html: string): string {
  const css = 'body{font:15px/1.6 Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;color:#16171a;margin:0;padding:0 2px}img{max-width:100%;height:auto}a{color:#2e44d6}h1{font-size:20px}pre{white-space:pre-wrap}@media (prefers-color-scheme:dark){body{color:#f0efec;background:#1a1b1e}a{color:#a6b6f5}}';
  return `<!doctype html><html><head><meta charset="utf-8"><base target="_blank"><style>${css}</style><title>${title.replace(/</g, '&lt;')}</title></head><body>${html}</body></html>`;
}

function ArticleView({ id }: { id: string }) {
  const article = useArticle(id);
  if (article.isLoading) return <p className="help-muted">Loading…</p>;
  if (article.isError || !article.data) return <p className="help-error" role="alert">This article did not load. {errorText(article.error)}</p>;
  const a = article.data;
  return (
    <div className="help-article">
      <div className="help-h-row"><h3>{a.title}</h3><a className="link-btn" href={a.url} target="_blank" rel="noreferrer">Open in a new tab</a></div>
      {/* allow-popups lets its links open in a new tab; no scripts, forms or same-origin access. */}
      <iframe title={a.title} sandbox="allow-popups allow-popups-to-escape-sandbox" srcDoc={articleDocument(a.title, a.body_html)} lang={helpLocale()} />
    </div>
  );
}

// ---- What's new ----

function WhatsNewView() {
  const entries = useWhatsNew();
  const markSeen = useMarkWhatsNewSeen();
  const unseen = entries.data?.unseen;
  useEffect(() => { if (unseen) void markSeen().catch(() => {}); }, [unseen]);
  if (entries.isLoading) return <p className="help-muted">Loading…</p>;
  if (entries.isError) return <p className="help-error" role="alert">What's new did not load. {errorText(entries.error)}</p>;
  const list = entries.data?.entries ?? [];
  if (list.length === 0) return <p className="help-muted">Nothing new yet.</p>;
  return (
    <ul className="help-list help-news">
      {list.map((e) => (
        <li key={e.id}>
          <span className="help-row-sub">{fmtDate(e.published_on)}</span>
          <span className="help-row-title">{e.title}</span>
          <span className="help-news-body">{e.body}{e.link && <> <a href={e.link} target="_blank" rel="noreferrer">Read more</a></>}</span>
        </li>
      ))}
    </ul>
  );
}
